import { isValidReminderLocalTime, normalizeReminderCues } from "./tracked-renewal";
import type { ReminderCue } from "./tracked-renewal";

export type NotificationHours = Readonly<{ startLocalTime: string; endLocalTime: string }>;

/** SCRUM-411 D20: an explicit initial policy, not a fallback for unavailable configuration. */
export const initialNotificationHours: NotificationHours = Object.freeze({
  startLocalTime: "07:00",
  endLocalTime: "21:00",
});

export function normalizeNotificationHours(value: NotificationHours): NotificationHours {
  if (
    !isValidReminderLocalTime(value.startLocalTime) ||
    !isValidReminderLocalTime(value.endLocalTime)
  )
    throw new Error("Choose valid start and end times in 24-hour HH:mm format.");
  return { startLocalTime: value.startLocalTime, endLocalTime: value.endLocalTime };
}

export function notificationTimeWithinHours(localTime: string, value: NotificationHours): boolean {
  if (!isValidReminderLocalTime(localTime)) throw new Error("Choose a valid notification time.");
  const hours = normalizeNotificationHours(value);
  return hours.startLocalTime <= hours.endLocalTime
    ? localTime >= hours.startLocalTime && localTime <= hours.endLocalTime
    : localTime >= hours.startLocalTime || localTime <= hours.endLocalTime;
}

type ReminderTiming = Readonly<{
  authoritativeDate: string;
  repeat?: "monthly" | undefined;
  eventLocalTime?: string | null | undefined;
  timeZone: string;
  reminderCues: ReadonlyArray<ReminderCue>;
}>;

/** Titles and recipients cannot invalidate previously confirmed notification times. */
export function reminderTimingChanged(before: ReminderTiming, after: ReminderTiming): boolean {
  const instructions = (value: ReminderTiming) => {
    const unique = new Map(
      value.reminderCues.map((cue) => [
        `${cue.offsetDays}:${cue.localTime}:${cue.minutesBeforeEvent ?? "fixed"}`,
        cue,
      ]),
    );
    return normalizeReminderCues([...unique.values()])
      .map((cue) => `${cue.offsetDays}:${cue.localTime}:${cue.minutesBeforeEvent ?? "fixed"}`)
      .sort()
      .join("|");
  };
  return (
    before.authoritativeDate !== after.authoritativeDate ||
    before.repeat !== after.repeat ||
    (before.eventLocalTime ?? null) !== (after.eventLocalTime ?? null) ||
    before.timeZone !== after.timeZone ||
    instructions(before) !== instructions(after)
  );
}
